import { Injectable } from '@angular/core';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { ReplaySubject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TENANT } from '../../config/identity-provider';
import { AuthCallbackError, AuthProvider, NEVER_RESOLVES } from './auth-provider';

const RELOGIN_ERRORS = [
  'login_required',
  'missing_refresh_token',
  'invalid_grant',
  'consent_required',
];

const CALLBACK_RETRY_KEY = 'fastr-auth0-callback-retry';

@Injectable()
export class Auth0AuthProvider implements AuthProvider {
  private auth0 = new Auth0Client({
    domain: environment.AUTH0_DOMAIN,
    clientId: environment.AUTH0_CLIENT_ID,
    cacheLocation: 'memory',
    useRefreshTokens: true,
    authorizationParams: {
      audience: environment.AUTH0_AUDIENCE,
      redirect_uri: environment.REDIRECT_UI,
    },
  });
  private authenticated = new ReplaySubject<boolean>(1);

  readonly isAuthenticated$ = this.authenticated.asObservable();

  async init(): Promise<void> {
    const params = new URLSearchParams(location.search);
    const isCallback =
      location.pathname === new URL(environment.REDIRECT_UI).pathname && params.has('state');

    if (isCallback && params.has('error')) {
      history.replaceState(null, '', location.pathname);
      throw new AuthCallbackError(
        params.get('error') ?? 'unknown_error',
        params.get('error_description'),
      );
    }

    if (isCallback && params.has('code')) {
      try {
        const { appState } = await this.auth0.handleRedirectCallback<{ returnTo?: string }>();
        sessionStorage.removeItem(CALLBACK_RETRY_KEY);
        history.replaceState(null, '', appState?.returnTo ?? '/');
      } catch {
        history.replaceState(null, '', '/');
        if (sessionStorage.getItem(CALLBACK_RETRY_KEY)) {
          throw new AuthCallbackError('callback_error', 'Failed to complete sign-in');
        }
        sessionStorage.setItem(CALLBACK_RETRY_KEY, '1');
        return this.redirectToLogin('/');
      }
    }

    if (!(await this.auth0.isAuthenticated())) {
      return this.redirectToLogin();
    }
    await this.getAccessToken();
    this.authenticated.next(true);
  }

  async getAccessToken(): Promise<string> {
    try {
      const token = await this.auth0.getTokenSilently();
      return token ?? this.redirectToLogin();
    } catch (error) {
      if (RELOGIN_ERRORS.includes((error as { error?: string })?.error ?? '')) {
        return this.redirectToLogin();
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    await this.auth0.logout({
      logoutParams: { returnTo: environment.LOGOUT_URL, federated: true },
    });
    this.authenticated.next(false);
  }

  private redirectToLogin(returnTo = location.pathname + location.search): Promise<never> {
    this.auth0.loginWithRedirect({
      appState: { returnTo },
      authorizationParams: { connection: TENANT },
    });
    return NEVER_RESOLVES();
  }
}
